import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PageTransition from '../../components/PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import NotificationCard from './components/NotificationCard';
import FilterToolbar from './components/FilterToolbar';

const NotificationsCenter = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all'
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [allNotifications, setAllNotifications] = useState([]);

  // Fetch ALL notifications (unfiltered) for counts, plus filtered for display
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const [allData, filteredData, count] = await Promise.all([
        notificationService?.getNotifications(user?.id, {}),
        notificationService?.getNotifications(user?.id, filters),
        notificationService?.getUnreadCount(user?.id)
      ]);
      setAllNotifications(allData || []);
      setNotifications(filteredData || []);
      setFilteredNotifications(filteredData || []);
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = notificationService?.subscribeToNotifications(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        const newNotif = payload?.new;
        setAllNotifications(prev => [newNotif, ...prev]);
        // Add to filtered list only if it matches current filters
        const matchesType = filters?.type === 'all' || newNotif?.type === filters?.type;
        const matchesStatus = filters?.status === 'all' || (filters?.status === 'unread' && !newNotif?.is_read) || (filters?.status === 'read' && newNotif?.is_read);
        if (matchesType && matchesStatus) {
          setNotifications(prev => [newNotif, ...prev]);
          setFilteredNotifications(prev => [newNotif, ...prev]);
        }
        if (!newNotif?.is_read) setUnreadCount(prev => prev + 1);
      } else if (payload?.eventType === 'UPDATE') {
        const updated = payload?.new;
        setAllNotifications(prev => prev?.map(n => n?.id === updated?.id ? updated : n));
        setNotifications(prev => prev?.map(n => n?.id === updated?.id ? updated : n));
        setFilteredNotifications(prev => prev?.map(n => n?.id === updated?.id ? updated : n));
        // Recalculate unread count
        notificationService?.getUnreadCount(user?.id)?.then(c => setUnreadCount(c || 0))?.catch(() => {});
      } else if (payload?.eventType === 'DELETE') {
        const deletedId = payload?.old?.id;
        setAllNotifications(prev => prev?.filter(n => n?.id !== deletedId));
        setNotifications(prev => prev?.filter(n => n?.id !== deletedId));
        setFilteredNotifications(prev => prev?.filter(n => n?.id !== deletedId));
        notificationService?.getUnreadCount(user?.id)?.then(c => setUnreadCount(c || 0))?.catch(() => {});
      }
    });

    return () => {
      channel?.unsubscribe();
    };
  }, [user?.id, filters]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSelectedIds([]);
  };

  // Handle mark as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService?.markAsRead(notificationId);
      // Optimistic update
      setAllNotifications(prev => prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n));
      setNotifications(prev => prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n));
      setFilteredNotifications(prev => prev?.map(n => n?.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle bulk mark as read
  const handleBulkMarkAsRead = async () => {
    if (selectedIds?.length === 0) return;
    try {
      await notificationService?.markMultipleAsRead(selectedIds);
      const unreadSelected = filteredNotifications?.filter(n => selectedIds?.includes(n?.id) && !n?.is_read)?.length;
      setAllNotifications(prev => prev?.map(n => selectedIds?.includes(n?.id) ? { ...n, is_read: true } : n));
      setNotifications(prev => prev?.map(n => selectedIds?.includes(n?.id) ? { ...n, is_read: true } : n));
      setFilteredNotifications(prev => prev?.map(n => selectedIds?.includes(n?.id) ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - unreadSelected));
      setSelectedIds([]);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService?.markAllAsRead(user?.id);
      setAllNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      setFilteredNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Handle delete
  const handleDelete = async (notificationId) => {
    try {
      await notificationService?.deleteNotification(notificationId);
      const wasUnread = allNotifications?.find(n => n?.id === notificationId && !n?.is_read);
      setAllNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      setFilteredNotifications(prev => prev?.filter(n => n?.id !== notificationId));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds?.length === 0) return;
    try {
      await notificationService?.deleteMultiple(selectedIds);
      const unreadSelected = allNotifications?.filter(n => selectedIds?.includes(n?.id) && !n?.is_read)?.length;
      setAllNotifications(prev => prev?.filter(n => !selectedIds?.includes(n?.id)));
      setNotifications(prev => prev?.filter(n => !selectedIds?.includes(n?.id)));
      setFilteredNotifications(prev => prev?.filter(n => !selectedIds?.includes(n?.id)));
      setUnreadCount(prev => Math.max(0, prev - unreadSelected));
      setSelectedIds([]);
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  // Handle select/deselect
  const handleSelect = (notificationId) => {
    setSelectedIds(prev => {
      if (prev?.includes(notificationId)) {
        return prev?.filter(id => id !== notificationId);
      }
      return [...prev, notificationId];
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedIds?.length === filteredNotifications?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotifications?.map(n => n?.id));
    }
  };

  // Build notification counts from ALL notifications (unfiltered)
  const notificationCounts = {
    all: allNotifications?.length,
    goal_achievement: allNotifications?.filter(n => n?.type === 'goal_achievement')?.length,
    badge_earned: allNotifications?.filter(n => n?.type === 'badge_earned')?.length,
    friend_milestone: allNotifications?.filter(n => n?.type === 'friend_milestone')?.length,
    activity_milestone: allNotifications?.filter(n => n?.type === 'activity_milestone')?.length,
    duel_invitation: allNotifications?.filter(n => n?.type === 'duel_invitation')?.length,
    duel_accepted: allNotifications?.filter(n => n?.type === 'duel_accepted')?.length,
    duel_completed: allNotifications?.filter(n => n?.type === 'duel_completed')?.length,
    friend_request: allNotifications?.filter(n => n?.type === 'friend_request')?.length,
    friend_accepted: allNotifications?.filter(n => n?.type === 'friend_accepted')?.length,
    squad_invitation: allNotifications?.filter(n => n?.type === 'squad_invitation')?.length,
    squad_member_joined: allNotifications?.filter(n => n?.type === 'squad_member_joined')?.length,
    challenge_invitation: allNotifications?.filter(n => n?.type === 'challenge_invitation')?.length,
    meal_reminder: allNotifications?.filter(n => n?.type === 'meal_reminder')?.length,
    sleep_insight: allNotifications?.filter(n => n?.type === 'sleep_insight')?.length,
  };

  // Separate priority and regular notifications
  const priorityNotifications = filteredNotifications?.filter(n => n?.priority && !n?.is_read);
  const regularNotifications = filteredNotifications?.filter(n => !n?.priority || n?.is_read);

  const readCount = allNotifications?.filter(n => n?.is_read)?.length;

  return (
    <>
      <Helmet>
        <title>Notifications Center - VitalTrack</title>
        <meta name="description" content="Manage your health notifications and alerts" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <QuickActionButton />

        <PageTransition>
          <main className="container mx-auto px-4 py-8 max-w-5xl">
            {/* Header Section */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="mb-8"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <motion.div
                    className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-xl wellness-elevation-2"
                    whileHover={{ scale: 1.08, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <Icon name="Bell" size={24} color="white" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Notifications Center</h1>
                    <p className="text-muted-foreground">Stay updated with your wellness journey</p>
                  </div>
                </div>
                {unreadCount > 0 && (
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAllAsRead}
                      iconName="CheckCheck"
                    >
                      Mark All Read
                    </Button>
                  </motion.div>
                )}
              </div>

              {/* Stats Bar */}
              <div className="flex items-center flex-wrap gap-3 mt-4">
                <motion.div
                  className="flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-lg border border-accent/30"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Icon name="Bell" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-accent">{allNotifications?.length} Total</span>
                </motion.div>
                {unreadCount > 0 && (
                  <motion.div
                    className="flex items-center space-x-2 px-4 py-2 bg-warning/10 rounded-lg border border-warning/30"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <Icon name="AlertCircle" size={16} className="text-warning" />
                    <span className="text-sm font-medium text-warning">{unreadCount} Unread</span>
                  </motion.div>
                )}
                {readCount > 0 && (
                  <motion.div
                    className="flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-lg border border-success/30"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm font-medium text-success">{readCount} Read</span>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Filter Toolbar */}
            <FilterToolbar
              filters={filters}
              onFilterChange={handleFilterChange}
              notificationCounts={notificationCounts}
            />

            {/* Bulk Actions */}
            <AnimatePresence>
              {selectedIds?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-xl flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckSquare" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-primary">
                      {selectedIds?.length} notification{selectedIds?.length > 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={handleBulkMarkAsRead} iconName="Check">Mark as Read</Button>
                    <Button variant="outline" size="sm" onClick={handleBulkDelete} iconName="Trash2">Delete</Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])} iconName="X">Cancel</Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Select All Checkbox */}
            {filteredNotifications?.length > 0 && (
              <div className="mb-4 flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedIds?.length === filteredNotifications?.length && filteredNotifications?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                />
                <label className="text-sm text-muted-foreground cursor-pointer" onClick={handleSelectAll}>
                  Select all {filteredNotifications?.length} notification{filteredNotifications?.length !== 1 ? 's' : ''}
                </label>
              </div>
            )}

            {/* Notifications List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center space-y-4">
                  <motion.div
                    className="rounded-full h-12 w-12 border-b-2 border-primary"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  <p className="text-muted-foreground">Loading notifications...</p>
                </div>
              </div>
            ) : filteredNotifications?.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-20 bg-card rounded-xl border border-border"
              >
                <div className="flex items-center justify-center w-20 h-20 bg-muted rounded-full mb-4">
                  <Icon name="BellOff" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">No notifications</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  {filters?.type !== 'all' || filters?.status !== 'all' ? 'No notifications match the current filters. Try adjusting your filters.' : "You're all caught up! Check back later for updates on your wellness journey."}
                </p>
                {(filters?.type !== 'all' || filters?.status !== 'all') && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => handleFilterChange({ type: 'all', status: 'all' })} iconName="X">Clear Filters</Button>
                )}
              </motion.div>
            ) : (
              <div className="space-y-4">
                {/* Priority Notifications */}
                {priorityNotifications?.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
                    <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-warning" />
                      <span>Priority Notifications</span>
                      <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-warning/10 text-warning">{priorityNotifications?.length}</span>
                    </h2>
                    <div className="space-y-3">
                      {priorityNotifications?.map((notification, index) => (
                        <NotificationCard
                          key={notification?.id}
                          notification={notification}
                          isSelected={selectedIds?.includes(notification?.id)}
                          onSelect={handleSelect}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          index={index}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Regular Notifications */}
                {regularNotifications?.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
                      <Icon name="Bell" size={16} className="text-muted-foreground" />
                      <span>All Notifications</span>
                      <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{regularNotifications?.length}</span>
                    </h2>
                    <div className="space-y-3">
                      {regularNotifications?.map((notification, index) => (
                        <NotificationCard
                          key={notification?.id}
                          notification={notification}
                          isSelected={selectedIds?.includes(notification?.id)}
                          onSelect={handleSelect}
                          onMarkAsRead={handleMarkAsRead}
                          onDelete={handleDelete}
                          index={index + priorityNotifications?.length}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </main>
        </PageTransition>
      </div>
    </>
  );
};

export default NotificationsCenter;